import type { ReactNode } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router"

import { HiisMark } from "@/brand/HiisMark"
import { STATUS_URL, apkUrl } from "@/brand/links"
import { PUBLIC_PAGES } from "@/features/pages"

const LINK =
  "rounded-sm transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"

function External({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={LINK}>
      {children}
    </a>
  )
}

/** The content pages (DV-158), each its own route on this site. */
function PageLinks() {
  const { t } = useTranslation("common")
  return PUBLIC_PAGES.map((p) => (
    <Link key={p.slug} to={p.path} className={LINK}>
      {t(`footer.${p.nameKey}`)}
    </Link>
  ))
}

/**
 * Under the sign-in, register and other public screens: the app download,
 * the status page, support (the deployment's support address, once
 * `getConfig` names one) and the content pages, then the HIIS mark: on
 * narrow screens only beside the sign-in's brand panel, always elsewhere.
 */
export function AuthFooter({
  supportEmail,
  mark = "narrow",
}: {
  supportEmail?: string | undefined
  mark?: "narrow" | "always"
}) {
  const { t } = useTranslation("common")
  return (
    <footer
      className={
        mark === "always"
          ? "mt-10 flex w-full max-w-3xl flex-col items-center gap-4"
          : "mt-10 flex w-full max-w-sm flex-col items-center gap-4"
      }
    >
      <nav
        aria-label={t("footer.label")}
        className="flex flex-wrap justify-center gap-x-4 gap-y-2 text-xs text-muted-foreground"
      >
        <External href={apkUrl()}>{t("footer.download")}</External>
        <External href={STATUS_URL}>{t("footer.status")}</External>
        {supportEmail ? (
          <External href={`mailto:${supportEmail}`}>{t("footer.support")}</External>
        ) : null}
        <PageLinks />
      </nav>
      <HiisMark
        className={mark === "always" ? "text-muted-foreground" : "text-muted-foreground lg:hidden"}
      />
    </footer>
  )
}

/** The signed-in app's slim footer: the same links, the API guide, and the support page. */
export function AppFooter() {
  const { t } = useTranslation("common")
  return (
    <footer className="mt-12 border-t border-border pt-4 print:hidden">
      <nav
        aria-label={t("footer.label")}
        className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-muted-foreground"
      >
        <External href={apkUrl()}>{t("footer.download")}</External>
        <Link to="/api-guide" className={LINK}>
          {t("footer.apiGuide")}
        </Link>
        <Link to="/account/support" className={LINK}>
          {t("footer.support")}
        </Link>
        <External href={STATUS_URL}>{t("footer.status")}</External>
        <PageLinks />
      </nav>
    </footer>
  )
}
